import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import SecureLogin from './pages/SecureLogin';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import SecuritySettings from './pages/SecuritySettings';
import Dashboard from './pages/Dashboard';
import EmployeeManagement from './pages/EmployeeManagement';
import TimeTracking from './pages/TimeTracking';
import CustomerManagement from './pages/CustomerManagement';
import Quotes from './pages/Quotes';
import Invoices from './pages/Invoices';
import MileageTracker from './pages/MileageTracker';
import CleaningCalculator from './pages/CleaningCalculator';
import Scheduling from './pages/Scheduling';
import Inventory from './pages/Inventory';
import Reports from './pages/Reports';
import Expenses from './pages/Expenses';
import Settings from './pages/Settings';
import About from './pages/About';
import CleaningInspection from './pages/CleaningInspection';
import Layout from './components/Layout';

// Stable module-level constant so React doesn't get a fresh array ref on
// every render and needlessly re-run ProtectedRoute's allowedRoles check.
const ADMIN_MANAGER_ROLES = ['admin', 'manager'];
// Employees CAN view Scheduling (read-only) so the "My Schedule" quick
// action on the Employee dashboard actually opens something. Write
// affordances (New Appointment / edit pencil / delete) are hidden inside
// Scheduling.js when role === 'employee'.
const SCHEDULING_ROLES = ['admin', 'manager', 'owner', 'employee'];

// --- Seller / Licensing Website pages (imported from external website) ---
import LandingPage from './pages/seller/LandingPage';
import PurchasePage from './pages/seller/PurchasePage';
import PaymentSuccessPage from './pages/seller/PaymentSuccessPage';
import UserAuth from './pages/seller/UserAuth';
import UserDashboard from './pages/seller/UserDashboard';
import UserSettings from './pages/seller/UserSettings';
import AdminLogin from './pages/seller/AdminLogin';
import AdminDashboardSecure from './pages/seller/AdminDashboardSecure';
import LicenseTestPage from './pages/seller/LicenseTestPage';

const ProtectedRoute = ({ children, allowedRoles }) => {
  // `loading=true` means AuthContext is still hydrating from localStorage /
  // bootstrapping the access token via the refresh cookie. If we redirect
  // during that window we lose the deep-linked route (e.g. /scheduling
  // bounces to /dashboard during cold-load reloads + Playwright automation).
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div
        data-testid="protected-route-loading"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '60vh', color: '#94a3b8', fontSize: 14,
        }}
      >
        Loading…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check both user.role and user.org_role for authorization
  const userRoles = [user.role, user.org_role].filter(Boolean);

  if (allowedRoles && !allowedRoles.some(role => userRoles.includes(role))) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  // Don't render the /login redirect until we know whether the user is
  // authenticated. Without this, a deep-linked refresh momentarily renders
  // /login while AuthContext is still hydrating, which can bounce the user
  // off their intended route.
  if (loading) {
    return (
      <div
        data-testid="app-routes-loading"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '100vh', color: '#94a3b8', fontSize: 14,
        }}
      >
        Loading…
      </div>
    );
  }

  return (
    <Routes>
      {/* === Public Seller / Licensing Website === */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/purchase" element={<PurchasePage />} />
      <Route path="/purchase/success" element={<PaymentSuccessPage />} />
      <Route path="/user/auth" element={<UserAuth />} />
      <Route path="/user" element={<UserDashboard />} />
      <Route path="/user/settings" element={<UserSettings />} />
      <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboardSecure />} />
      <Route path="/license-test" element={<LicenseTestPage />} />

      {/* === Maidly Business App auth === */}
      <Route path="/login" element={!user ? <Login /> : <Navigate to="/dashboard" replace />} />
      <Route path="/secure-login" element={<SecureLogin />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* === Maidly Business App (protected) === */}
      <Route path="/app" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" replace />} />
      </Route>
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="employees" element={<ProtectedRoute allowedRoles={ADMIN_MANAGER_ROLES}><EmployeeManagement /></ProtectedRoute>} />
        <Route path="crews" element={<Navigate to="/employees?tab=crews" replace />} />
        <Route path="time-tracking" element={<TimeTracking />} />
        <Route path="customers" element={<ProtectedRoute allowedRoles={ADMIN_MANAGER_ROLES}><CustomerManagement /></ProtectedRoute>} />
        <Route path="quotes" element={<ProtectedRoute allowedRoles={ADMIN_MANAGER_ROLES}><Quotes /></ProtectedRoute>} />
        <Route path="invoices" element={<ProtectedRoute allowedRoles={ADMIN_MANAGER_ROLES}><Invoices /></ProtectedRoute>} />
        <Route path="recurring-invoices" element={<Navigate to="/invoices?tab=recurring" replace />} />
        <Route path="expenses" element={<ProtectedRoute allowedRoles={ADMIN_MANAGER_ROLES}><Expenses /></ProtectedRoute>} />
        <Route path="mileage" element={<MileageTracker />} />
        <Route path="calculator" element={<ProtectedRoute allowedRoles={ADMIN_MANAGER_ROLES}><CleaningCalculator /></ProtectedRoute>} />
        <Route path="scheduling" element={<ProtectedRoute allowedRoles={SCHEDULING_ROLES}><Scheduling /></ProtectedRoute>} />
        <Route path="inventory" element={<ProtectedRoute allowedRoles={ADMIN_MANAGER_ROLES}><Inventory /></ProtectedRoute>} />
        <Route path="inspections" element={<CleaningInspection />} />
        <Route path="reports" element={<ProtectedRoute allowedRoles={ADMIN_MANAGER_ROLES}><Reports /></ProtectedRoute>} />
        <Route path="reports-taxes" element={<Navigate to="/expenses?tab=reports" replace />} />
        <Route path="storage" element={<Navigate to="/settings?tab=storage" replace />} />
        <Route path="settings" element={<ProtectedRoute allowedRoles={ADMIN_MANAGER_ROLES}><Settings /></ProtectedRoute>} />
        <Route path="security" element={<ProtectedRoute allowedRoles={ADMIN_MANAGER_ROLES}><SecuritySettings /></ProtectedRoute>} />
        <Route path="about" element={<About />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
